
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { CarrierT } from '@/core/types/carriers.types'

// realisations
import { carriers } from '@/core/realisations/carriers.realisation'

// constants
import { STATUSES_RPK_RPP } from '@/core/constants/orders.constants'
import { CityT } from '@/core/types/cities.types'
import { cities } from '@/core/realisations/cities.realisation'
import { UserT } from '@/core/types/users.types'
import { users } from '@/core/realisations/users.realisation'

export default defineComponent({
    components: {
        'the-input': TheInput,
        'the-select': TheSelect,
        'the-button': TheButton,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['reset', 'search'],
    setup(props, { emit }) {
        const state = reactive({
            id: undefined as number | undefined,
            ftl_order_id: undefined as number | undefined,
            turnover_date_from: undefined as string | undefined,
            turnover_date_to: undefined as string | undefined,
            company_id: undefined as number | undefined,
            act_number: undefined as string | undefined,
            act_date: undefined as string | undefined,
            status_id: undefined as number | undefined,
            from_city_id: undefined as number | undefined,
            where_city_id: undefined as number | undefined,
            user_id: undefined as number | undefined,
        })

        const data_state = reactive({
            carriers: new DataState<CarrierT>({}),
            cities: new DataState<CityT>({}),
            logists: new DataState<UserT>({}),
        })

        const reset = () => {
            state.id = undefined
            state.ftl_order_id = undefined
            state.turnover_date_from = undefined
            state.turnover_date_to = undefined
            state.company_id = undefined
            state.act_number = undefined
            state.act_date = undefined
            state.status_id = undefined
            state.from_city_id = undefined
            state.where_city_id = undefined
            state.user_id = undefined

            emit('reset')
        }

        const search = () => {
            emit('search', {
                id: state.id,
                ftl_order_id: state.ftl_order_id,
                turnover_date_from: state.turnover_date_from,
                turnover_date_to: state.turnover_date_to,
                company_id: state.company_id,
                act_number: state.act_number,
                act_date: state.act_date,
                status_id: state.status_id,
                from_city_id: state.from_city_id,
                where_city_id: state.where_city_id,
                user_id: state.user_id,
            })
        }

        const loadCompanies = async (page: number, search?: string) => {
            data_state.carriers.pagination.current_page = page

            try {
                data_state.carriers.is_loading = true
                const response = await carriers.getList({ page, search, statusId: 2 })
                data_state.carriers.list = response.data

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.carriers.pagination.total_pages) return
            data_state.carriers.pagination.current_page = page

            try {
                data_state.carriers.is_loading = true

                const response = await carriers.getList({ page, statusId: 2 })
                data_state.carriers.list = data_state.carriers.list.concat(response.data)

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CarrierT) => {
            await loadCompanies(data_state.carriers.pagination.current_page)
            data_state.carriers.list.unshift(company)
        }

        const loadCities = async (page: number, search?: string) => {
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const data = await cities.getAllList(page, search, 9999)
                data_state.cities.list = data.data

                data_state.cities.pagination.total_pages = data.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const loadLogists = async (page: number, name?: string) => {
            data_state.logists.pagination.current_page = page

            try {
                data_state.logists.is_loading = true

                const logists = await users.getLogists(page, name, 0)

                data_state.logists.list = logists.data

                data_state.logists.pagination.total_pages = logists.meta.last_page

                data_state.logists.is_failed = false
            } catch (error) {
                data_state.logists.is_failed = true
                console.error(error)
            } finally {
                data_state.logists.is_loading = false
            }
        }

        const loadMoreLogists = async (page: number) => {
            if (page > data_state.logists.pagination.total_pages) return
            data_state.logists.pagination.current_page = page

            try {
                data_state.logists.is_loading = true

                const logists = await users.getLogists(page, undefined, 0)
                data_state.logists.list = data_state.logists.list.concat(logists.data)

                data_state.logists.pagination.total_pages = logists.meta.last_page

                data_state.logists.is_failed = false
            } catch (error) {
                data_state.logists.is_failed = true
                console.error(error)
            } finally {
                data_state.logists.is_loading = false
            }
        }

        const searchLogists = async (search_query: string) => {
            loadLogists(1, search_query)
        }

        const unshiftFindedLogist = async (logist: UserT) => {
            await loadLogists(data_state.logists.pagination.current_page)
            data_state.logists.list.unshift(logist)
        }

        onBeforeMount(() => {
            loadCities(1)
            loadCompanies(1)
            loadLogists(1)
        })

        return {
            state,
            data_state,
            // carriers
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            // form
            reset,
            search,
            loadCities,
            loadLogists,
            loadMoreLogists,
            searchLogists,
            unshiftFindedLogist,
            // constants
            STATUSES_RPK_RPP,
        }
    },
})
